/**
 * Twin correlation GROUPS resource — typed wrapper over
 * `/v1/twin/correlation-groups` (Wave C, Phase 1).
 *
 * A correlation group is a named, mechanism-tagged matrix of INPUT process
 * params (rows) × OUTCOME defects (cols), correlated WITHIN the group only — so
 * cross-group, physically-meaningless pairs are never computed. Two kinds, one
 * shape: STANDARD groups (`is_standard`) are the 8 council causal groups, defined
 * in Go and read-only; CUSTOM groups are org-authored (twin_correlation_groups,
 * migration 0123) and fully editable.
 */

import { api } from "@/lib/api-client";
import type { DefectCell } from "@foundry/CorrelationHeatmap";
import type { ScatterSample } from "@/lib/widgets/__foundry_fixtures";
import { qs } from "./qs";

/** An input several instruments give on this plant, none of which the analysis can trust over the others: measured,
 *  but withheld, with why (the server's words), never "not measured here". */
export interface WithheldInput {
  param: string;
  reason: string;
  /** How many instruments give it. */
  entities: number;
}

/** One instrument that gave some of an input's casting lots (the per-lot fallback): its equipment's name, else its
 *  tag's, how many of the joined lots it gave, and the first and last of their plant days ("2026-10-05"). */
export interface SourcedInputSource {
  name: string;
  entity_id?: string;
  tag_id: string;
  lots: number;
  from: string;
  to: string;
}

/** An input more than one instrument gave the lots of: its sources, in the order the analysis trusts them. */
export interface SourcedInput {
  param: string;
  sources: SourcedInputSource[];
}

/** An input of a lot-grain matrix that the analysis worked out from measured values instead of recording it (a casting
 *  lot's carbon equivalent, from its heats' own carbon, silicon and phosphorus). A plant's own recorded value has none. */
export interface DerivedInput {
  /** The input as the matrix names it ("CE"). */
  param: string;
  /** In plain words, as the brief labels it: "Carbon equivalent (CE), worked out from carbon, silicon and phosphorus". */
  label: string;
  /** How it was worked out, alone: "carbon equivalent, worked out from carbon, silicon and phosphorus". */
  note: string;
}

/** One correlation group (standard or custom). */
/** What a group can show on this plant's data (correlation_groups_lots.go): the grain its data meets at, the
 *  observations there, and its declared inputs and outcomes the data carries or lacks. */
export interface GroupAvailability {
  grain: "heat" | "lot";
  /** The most observations any input-outcome pair has: observations carrying both. */
  n: number;
  /** The most distinct input values any pair has among them: a value carried across observations counts once. */
  distinct: number;
  inputs: string[];
  outcomes: string[];
  missing_inputs: string[];
  missing_outcomes: string[];
  available: boolean;
  /** Inputs joined to the casting lots on a lag the plant has not confirmed (the analysis run's lag windows): the
   *  group answers on them, and its cells are exploratory. */
  exploratory_inputs?: string[];
  /** Inputs several instruments give and none stands: withheld, each with why. */
  withheld_inputs?: WithheldInput[];
  /** Inputs whose lots more than one instrument gave: which gave which lots, how many and over which plant days. */
  sourced_inputs?: SourcedInput[];
  /** Inputs the plant measures on a line with no lag to the pour yet: measured, never "missing". */
  no_lag_inputs?: string[];
}

export interface CorrelationGroup {
  id: string;
  name: string;
  mechanism: string;
  input_params: string[];
  outcome_defects: string[];
  /** True for the 8 Go-defined council groups — read-only product content. */
  is_standard: boolean;
  /** "grey_iron" | "ductile_iron" | "" (both). A ductile-only group (e.g. the
   *  treatment-nodularity group) never renders for a grey org. */
  archetype?: string;
  /** The identity this group correlates AT: "heat" | "ladle" | "mould" | "shift" |
   *  "ht_batch" (the same tokens the windowed store stamps). Empty on a custom group. */
  grain?: string;
  /** Whether the mechanism is diagnosable within one heat ("within_heat") or only as
   *  an across-heats trend ("across_heats"); "custom" for a user-authored group. */
  scope?: string;
  /** Set by the list: what the group can show on this plant's data. */
  availability?: GroupAvailability;
}

/**
 * The within-group correlation matrix for one group over the org's heats.
 *
 * Honesty affordances the all-vs-all matrix lacks:
 *  - `cells` carries ONLY supported + significant pairs (m ≥ 3 heats, p ≤ 0.10);
 *    a withheld pair is omitted → the heatmap renders a dashed "—".
 *  - an input the org never measured, or an outcome that never occurred, is
 *    DROPPED from the axis into `droppedInputs` / `droppedOutcomes` ("not measured
 *    at this plant"), never a fabricated zero-variance row.
 */
export interface CorrelationGroupMatrix {
  group: CorrelationGroup;
  /** Row axis — the group's declared inputs resolved to the vocabulary the heats
   *  carry (dropped inputs removed). */
  params: string[];
  paramLabels: Record<string, string>;
  /** Column axis — the group's declared outcomes actually observed in the heats. */
  defects: string[];
  defectLabels: Record<string, string>;
  cells: DefectCell[];
  /** Per defect×param scatter, keyed `${defect}|${param}`. */
  scatter: Record<string, ScatterSample[]>;
  droppedInputs: string[];
  droppedOutcomes: string[];
  /** Number of observations the matrix was computed over: heats, or casting lots (grain). */
  n: number;
  method: "pearson" | "spearman";
  /** Where this group's outcomes are booked, so where the matrix read: the heats, or the casting lots. */
  grain?: "heat" | "lot";
  /** On a lot-grain matrix, what one observation is (the server's words). */
  grain_note?: string;
  /** Inputs joined on a lag the plant has not confirmed, and what that means (the server's words). */
  exploratory_inputs?: string[];
  exploratory_note?: string;
  /** Inputs whose lots more than one instrument gave: which gave which lots, how many and over which plant days. */
  sourced_inputs?: SourcedInput[];
  /** What it means that each lot's value is from the first instrument that measured it (the server's words). */
  sourced_note?: string;
  /** Inputs several instruments give and none stands: withheld, each with why. */
  withheldInputs?: WithheldInput[];
  /** Inputs the plant measures on a line with no lag to the pour yet: measured, never "not measured here". */
  noLagInputs?: string[];
  /** Inputs worked out from measured values, not recorded (2026-10-28), each with the brief's label of it. */
  derived_inputs?: DerivedInput[];
}

/** Create / update body for a CUSTOM group. */
export interface CorrelationGroupInput {
  name: string;
  mechanism?: string;
  input_params: string[];
  outcome_defects: string[];
  /** "grey_iron" | "ductile_iron" | "" (both). */
  archetype?: string;
}

/** Matrix query params — mirror GetCorrelations (method + date window). */
export type CorrelationGroupMatrixParams = {
  method?: "pearson" | "spearman";
  from?: string;
  to?: string;
};

interface GroupList {
  items: CorrelationGroup[];
  total: number;
}

export const twinCorrelationGroups = {
  /** The 8 standard council groups merged with the org's custom groups. */
  list: () => api.get<GroupList>("/v1/twin/correlation-groups"),
  /** One group's within-group matrix over the org's heats. */
  matrix: (id: string, params?: CorrelationGroupMatrixParams) =>
    api.get<CorrelationGroupMatrix>(
      `/v1/twin/correlation-groups/${id}/matrix${qs(params)}`,
    ),
  /** Author a custom group. Name + ≥1 input + ≥1 outcome required. */
  create: (body: CorrelationGroupInput) =>
    api.post<CorrelationGroup>("/v1/twin/correlation-groups", body),
  /** Edit a custom group (a standard "std-*" group is read-only → 400). */
  update: (id: string, body: CorrelationGroupInput) =>
    api.patch<CorrelationGroup>(`/v1/twin/correlation-groups/${id}`, body),
  /** Delete a custom group (a standard "std-*" group is read-only → 400). */
  remove: (id: string) =>
    api.delete<{ deleted: boolean; id: string }>(
      `/v1/twin/correlation-groups/${id}`,
    ),
};
